import { useRef } from 'react';
import type { ReactNode } from 'react';
import If from '@/components/If';
import {
  TransitionWrapper,
  ListingColumn,
  ListingRow,
  UniqueSymbol,
  ToggleWrapper,
} from './styled';
import { Transition } from 'react-transition-group';
import { useTracking } from 'react-tracking';
import { Box } from '@simplywallst/ui-core';
import { CountryFlag } from '@/components/CountryFlag';

import type { SearchResult } from '@/hooks/useSearch';

type Listing = SearchResult['listings'][number];

interface Props {
  light?: boolean;
  fullMatch: Listing[];
  countryMatch: Listing[];
  listings: Listing[];
  expand: boolean;
  secondaryWrapper?: (child: ReactNode, listing: Listing) => ReactNode;
  toggleElement: ReactNode;
}

export const SecondaryListings = ({
  light,
  fullMatch,
  countryMatch,
  listings,
  expand,
  secondaryWrapper,
  toggleElement,
}: Props) => {
  const { trackEvent } = useTracking();
  const expandableMenuRef = useRef<HTMLUListElement | null>(null);
  const listingMapper =
    (canShowToggle = false) =>
    (listing: Listing, index: number, self: Listing[]) => {
      if (typeof secondaryWrapper === 'function') {
        return (
          <li key={listing.uniqueSymbol}>
            {secondaryWrapper(
              <ListingRow
                light={light}
                alignContent="center"
                onClick={() => {
                  trackEvent({
                    action: 'listing-click',
                    company: listing.uniqueSymbol,
                  });
                }}
              >
                <ListingColumn displayFlex noWrap alignItem="center">
                  <Box minWidth={68} />
                  <CountryFlag countryISO={listing.exchangeCountryIso} />

                  <UniqueSymbol light={light}>
                    {listing.uniqueSymbol}
                  </UniqueSymbol>
                  <ToggleWrapper>
                    {canShowToggle &&
                      index === self.length - 1 &&
                      listings.length > 0 &&
                      toggleElement}
                  </ToggleWrapper>
                </ListingColumn>
              </ListingRow>,
              listing
            )}
          </li>
        );
      }
      return null;
    };

  return (
    <>
      <If match={countryMatch.length > 0}>
        {() => (
          <ul>{countryMatch.map(listingMapper(fullMatch.length === 0))}</ul>
        )}
      </If>
      <If match={fullMatch.length > 0}>
        {() => <ul>{fullMatch.map(listingMapper(true))}</ul>}
      </If>
      <Transition in={expand} timeout={80} unmountOnExit mountOnEnter appear>
        {(status) => (
          <TransitionWrapper
            transitionStatus={status}
            duration={80}
            height={expandableMenuRef.current?.getBoundingClientRect().height}
          >
            <ul ref={expandableMenuRef}>{listings.map(listingMapper())}</ul>
          </TransitionWrapper>
        )}
      </Transition>
    </>
  );
};
